import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Save, Loader2 } from "lucide-react";

/**
 * RescheduleCutoffSetting — small inline widget for a coach to set their
 * global reschedule/cancel cutoff (hours before start). Falls back to 48h if
 * they've never set it. Per-offering overrides live on the offering form.
 */
export default function RescheduleCutoffSetting({ initialValue = null, className = "" }) {
  const [value, setValue] = useState(initialValue ?? 48);
  const [loading, setLoading] = useState(initialValue === null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialValue !== null) return;
    api.get("/auth/me").then((r) => {
      const raw = r.data?.reschedule_cutoff_hours;
      setValue(typeof raw === "number" ? raw : 48);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [initialValue]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/coach/settings/reschedule-cutoff", { reschedule_cutoff_hours: Math.max(0, Number(value) || 0) });
      toast.success(`Cutoff set to ${value}h`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className={`card-surface p-4 flex items-center gap-3 flex-wrap ${className}`} data-testid="reschedule-cutoff-setting">
      <Clock className="w-4 h-4 text-[#CCFF00]" />
      <div className="flex-1 min-w-[180px]">
        <div className="label-mono">Reschedule / cancel cutoff</div>
        <div className="text-[10px] text-[#525252] mt-0.5">
          Applies to all your sessions — players inside this window need your override to change or cancel. Individual offerings can override this.
        </div>
      </div>
      <input
        type="number"
        min="0"
        max="720"
        step="1"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={loading || saving}
        className="w-20 bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-2 py-1.5 text-sm text-right outline-none"
        data-testid="reschedule-cutoff-input"
      />
      <span className="text-xs text-[#A3A3A3]">hours</span>
      <button
        onClick={save}
        disabled={saving || loading}
        className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1 disabled:opacity-50"
        data-testid="reschedule-cutoff-save"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        Save
      </button>
    </div>
  );
}
